
import { Button } from "@/components/ui/button";
import { StoreService } from "@/gen/omnios/v1/store_connect";
import { useAuthStore } from "@/lib/auth";
import { transport } from "@/lib/grpc";
import { usePortalDetail } from "@/routes/portals.$id";
import { createClient } from "@connectrpc/connect";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
	Flame,
	Heart,
	Layers,
	ShoppingCart,
	Clock,
	Gift,
	RefreshCw,
} from "lucide-react";

export const Route = createFileRoute("/portals/$id/recommendations")({
	component: PortalRecommendations,
});

const storeClient = createClient(StoreService, transport);

interface RecProduct {
	id: string;
	title: string;
	slug: string;
	brand: string;
	price_cents: number | null;
	currency: string;
	score: number;
	reason: string;
}

const DEFAULT_SURFACES = [
	{ id: "trending", icon: Flame, color: "text-orange-500" },
	{ id: "for-you", icon: Heart, color: "text-pink-500" },
	{ id: "similar", icon: Layers, color: "text-blue-500" },
	{ id: "bought-together", icon: ShoppingCart, color: "text-green-500" },
	{ id: "recently-viewed", icon: Clock, color: "text-violet-500" },
	{ id: "cart-offers", icon: ShoppingCart, color: "text-amber-500" },
	{ id: "post-purchase", icon: Gift, color: "text-teal-500" },
] as const;

const SURFACE_ICONS: Record<string, typeof Flame> = {
	product_page: Layers,
	cart: ShoppingCart,
	home: Flame,
	checkout: ShoppingCart,
	post_purchase: Gift,
	dashboard: Flame,
	po_detail: Layers,
	order_detail: Layers,
	analytics: Heart,
};

function surfaceKey(id: string) {
	return id.replace(/-/g, "_");
}

function PortalRecommendations() {
	const { portal, portalApiUrl, keys } = usePortalDetail();
	const { t } = useTranslation("portals");
	const token = useAuthStore((s) => s.token);
	const qc = useQueryClient();
	const [previewSurface, setPreviewSurface] = useState<string | null>(null);
	const activeKey = keys.find((k) => !k.revokedAt);

	const apiHeaders: HeadersInit = activeKey?.tokenPrefix
		? { "X-API-Key": activeKey.tokenPrefix }
		: token
			? { Authorization: `Bearer ${token}` }
			: {}

	const { data: funnel } = useQuery({
		queryKey: ["portal-funnel", portalApiUrl, "30"],
		queryFn: async () => {
			const res = await fetch(
				`${portalApiUrl}/api/v1/portal/analytics/funnel?days=30`,
				{ headers: apiHeaders },
			)
			if (!res.ok) return null;
			return res.json() as Promise<{ searches: number; product_views: number }>;
		},
		enabled: !!portalApiUrl,
	})

	const totalEvents = (funnel?.searches ?? 0) + (funnel?.product_views ?? 0);
	const isActive = totalEvents >= 100;

	// Derive surfaces from portal_type config, fallback to defaults
	const surfaces = useMemo(() => {
		const configSurfaces = portal?.portalTypeConfig?.surfaces as string[] | undefined;
		if (configSurfaces && configSurfaces.length > 0) {
			return configSurfaces.map((s) => ({
				id: s,
				icon: SURFACE_ICONS[s] ?? Flame,
				color: "text-orange-500",
			}));
		}
		return [...DEFAULT_SURFACES];
	}, [portal?.portalTypeConfig]);

	const { data: previewProducts, isLoading: previewLoading } = useQuery<
		RecProduct[]
	>({
		queryKey: ["portal-rec-preview", portalApiUrl, previewSurface],
		queryFn: async () => {
			if (!previewSurface || !portalApiUrl) return [];
			const res = await fetch(
				`${portalApiUrl}/api/v1/portal/recommendations/${previewSurface}?limit=6`,
				{ headers: apiHeaders },
			)
			if (!res.ok) return [];
			const data = await res.json();
			return data.products ?? data.entities ?? [];
		},
		enabled: !!previewSurface && !!portalApiUrl,
	})

	const refreshMutation = useMutation({
		mutationFn: () =>
			storeClient.refreshRecommendations({
				portalInstanceId: portal?.id ?? "",
			}),
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: ["portal-rec-preview"] });
			qc.invalidateQueries({ queryKey: ["portal-funnel"] });
		},
	});

	return (
		<div className="space-y-6">
			<div className="flex items-center justify-between">
				<div>
					<h2 className="text-lg font-semibold">{t("recommendations")}</h2>
					<p className="text-sm text-muted-foreground mt-1">
						{t("recommendations_desc")}
					</p>
				</div>
				<Button
					variant="outline"
					size="sm"
					className="gap-2"
					onClick={() => refreshMutation.mutate()}
					disabled={refreshMutation.isPending}
				>
					<RefreshCw className={`h-3.5 w-3.5 ${refreshMutation.isPending ? "animate-spin" : ""}`} />
					{t("rec_refresh")}
				</Button>
			</div>

			{/* Status */}
			<div
				className={`rounded-lg border px-4 py-3 flex items-center gap-3 ${isActive ? "border-emerald-500/30 bg-emerald-500/5" : "border-amber-500/30 bg-amber-500/5"}`}
			>
				<div
					className={`h-2 w-2 rounded-full ${isActive ? "bg-emerald-500" : "bg-amber-500"}`}
				/>
				<div>
					<p className="text-sm font-medium">
						{isActive ? t("rec_status_active") : t("rec_status_inactive")}
					</p>
					{!isActive && (
						<p className="text-xs text-muted-foreground">
							{t("rec_status_threshold")}
						</p>
					)}
				</div>
			</div>

			{/* Surfaces Grid */}
			<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
				{surfaces.map((surface) => {
					const Icon = surface.icon;
					const key = surfaceKey(surface.id);
					const isSelected = previewSurface === surface.id;
					return (
						<div
							key={surface.id}
							className={`rounded-lg border p-4 transition-colors ${isSelected ? "border-accent ring-1 ring-accent/30" : "hover:border-foreground/20"}`}
						>
							<div className="flex items-start gap-3">
								<div
									className={`mt-0.5 rounded-md bg-muted p-2 ${surface.color}`}
								>
									<Icon className="h-4 w-4" />
								</div>
								<div className="flex-1 min-w-0">
									<p className="text-sm font-medium">
										{t(`rec_surface_${key}`, key)}
									</p>
									<p className="text-xs text-muted-foreground mt-0.5">
										{t(`rec_surface_${key}_desc`, "")}
									</p>
								</div>
							</div>
							<div className="mt-3 flex items-center justify-end">
								<Button
									variant="link"
									size="sm"
									onClick={() =>
										setPreviewSurface(isSelected ? null : surface.id)
									}
									className="text-xs text-accent h-auto p-0"
								>
									{isSelected ? t("hide") : t("rec_preview")}
								</Button>
							</div>
						</div>
					)
				})}
			</div>

			{/* Preview Panel */}
			{previewSurface && (
				<div className="rounded-lg border p-4">
					<h3 className="text-sm font-medium mb-3">
						{t("rec_preview")}:{" "}
						{t(`rec_surface_${surfaceKey(previewSurface)}`, previewSurface)}
					</h3>
					{previewLoading ? (
						<div className="grid grid-cols-2 md:grid-cols-3 gap-3">
							{Array.from({ length: 6 }).map((_, i) => (
								<div
									key={i}
									className="rounded-lg border p-3 animate-pulse bg-muted/30 h-20"
								/>
							))}
						</div>
					) : previewProducts && previewProducts.length > 0 ? (
						<div className="grid grid-cols-2 md:grid-cols-3 gap-3">
							{previewProducts.map((p) => (
								<div
									key={p.id}
									className="rounded-lg border p-3 flex flex-col gap-1"
								>
									<p className="text-sm font-medium truncate">{p.title}</p>
									{p.brand && (
										<p className="text-xs text-muted-foreground">{p.brand}</p>
									)}
									<div className="flex items-center justify-between mt-auto pt-1">
										{p.price_cents != null && (
											<span className="text-xs font-medium">
												{(p.price_cents / 100).toLocaleString()}{" "}
												{p.currency === "RUB" ? "₽" : p.currency}
											</span>
										)}
										<span className="text-[10px] text-muted-foreground">
											{t("rec_score")}: {(p.score * 100).toFixed(0)}%
										</span>
									</div>
								</div>
							))}
						</div>
					) : (
						<p className="text-sm text-muted-foreground text-center py-6">
							{t("rec_no_data")}
						</p>
					)}
				</div>
			)}
		</div>
	)
}
